/**
 * Local persistence for the terminal chat transcripts, keyed per paradigm
 * user. Mirrors the localStorage conventions in `agent-url.ts`.
 *
 * Goal: Telegram-style continuity. A reload paints the most recent messages
 * immediately so the user has something to scroll while the gateway
 * reconnects.
 *
 * OpenClaw's `chat.history` RPC is a tail-reader with no cursor pagination
 * (hard max 1000 messages), so the local cache is the primary source of
 * deeper history — caps are intentionally generous to retain weeks of
 * conversation across reloads.
 *
 * Sessions: each `ChatSession` has its own per-session message blob keyed by
 * `{userId}:paradigm_terminal_chat_history:{sessionId}`. The sessions index
 * lives under `{userId}:paradigm_terminal_sessions`. Existing users who had
 * the legacy flat-history blob get a one-shot migration into a default
 * "Restored chat" session under the Paradex folder.
 */

import type {
  ChatMessage,
  ChatSession,
  CustomFolder,
  SessionFolder,
} from './types';

const HISTORY_KEY_SUFFIX = 'paradigm_terminal_chat_history';
const SESSIONS_KEY_SUFFIX = 'paradigm_terminal_sessions';
const UI_STATE_KEY_SUFFIX = 'paradigm_terminal_history_ui';
const CUSTOM_FOLDERS_KEY_SUFFIX = 'paradigm_terminal_custom_folders';
const TOMBSTONES_KEY_SUFFIX = 'paradigm_terminal_deleted';

// Global (not per-user) — listed once at sign-in so the cleanup pass can
// scan across users without iterating localStorage by prefix.
const PENDING_CLEANUP_KEY = 'paradigm_terminal_pending_cleanup';

/**
 * How long persisted messages stay in localStorage. 30 days keeps roughly a
 * month of conversation visible across reloads — important because OpenClaw's
 * `chat.history` RPC has no cursor pagination, so anything dropped from this
 * cache and rolled out of the gateway's 1000-message tail is unrecoverable.
 */
export const PERSISTED_HISTORY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Lower bound on persisted messages so a long-idle user (vacation, long
 * weekend) still sees their last conversation on reload even if it's older
 * than the retention window.
 */
export const PERSISTED_HISTORY_MIN_MESSAGES = 50;

/**
 * Absolute safety cap. Per-origin localStorage caps at ~5MB and a single
 * chat message can easily exceed 1KB (assistant replies with full
 * portfolio analyses run several KB each). Keep this bounded well below
 * the 5MB origin budget so other features sharing localStorage have room.
 */
export const PERSISTED_HISTORY_HARD_CAP = 1500;

export interface HistoryUiState {
  readonly collapsed: {
    readonly paradex: boolean;
    readonly paradigm: boolean;
  };
  readonly activeSessionId: string | null;
}

const DEFAULT_UI_STATE: HistoryUiState = {
  collapsed: { paradex: false, paradigm: false },
  activeSessionId: null,
};

const legacyHistoryKey = (userId: string): string =>
  `${userId.toLowerCase()}:${HISTORY_KEY_SUFFIX}`;

// Original `<suffix>:<userId>` layout, kept for one-shot migration on read.
const veryLegacyHistoryKey = (userId: string): string =>
  `${HISTORY_KEY_SUFFIX}:${userId.toLowerCase()}`;

const sessionsKey = (userId: string): string =>
  `${userId.toLowerCase()}:${SESSIONS_KEY_SUFFIX}`;

const customFoldersKey = (userId: string): string =>
  `${userId.toLowerCase()}:${CUSTOM_FOLDERS_KEY_SUFFIX}`;

const sessionMessagesKey = (userId: string, sessionId: string): string =>
  `${userId.toLowerCase()}:${HISTORY_KEY_SUFFIX}:${sessionId}`;

const uiStateKey = (userId: string): string =>
  `${userId.toLowerCase()}:${UI_STATE_KEY_SUFFIX}`;

const tombstonesKey = (userId: string): string =>
  `${userId.toLowerCase()}:${TOMBSTONES_KEY_SUFFIX}`;

/**
 * Deletion marker for session-sync. Structurally identical to
 * `Tombstone` in `@paradigm/terminal-sync` — kept local so this storage
 * module stays dependency-free. Tombstones ride inside the synced index
 * document so a deletion on this device beats a stale index from another
 * one; locally they survive reloads so offline deletions aren't lost.
 */
export interface DeletionTombstone {
  readonly kind: 'session' | 'folder';
  readonly id: string;
  readonly deletedAt: number;
}

export function loadTombstones(userId: string): readonly DeletionTombstone[] {
  try {
    const raw = localStorage.getItem(tombstonesKey(userId));
    if (raw == null || raw.length === 0) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPersistedTombstone);
  } catch {
    return [];
  }
}

export function saveTombstones(
  userId: string,
  tombstones: readonly DeletionTombstone[],
): void {
  try {
    localStorage.setItem(tombstonesKey(userId), JSON.stringify(tombstones));
  } catch {
    // best-effort
  }
}

export function addTombstone(
  userId: string,
  kind: DeletionTombstone['kind'],
  id: string,
): void {
  const existing = loadTombstones(userId).filter(
    (t) => !(t.kind === kind && t.id === id),
  );
  saveTombstones(userId, [...existing, { kind, id, deletedAt: Date.now() }]);
}

/**
 * Bounded-retention bookkeeping for the sign-out flush-failure path.
 *
 * Sign-out flushes pending sync uploads first and only wipes chat history
 * once they drain. If the gateway is unreachable at sign-out, the data
 * stays in localStorage so it can be re-uploaded on the next sign-in —
 * otherwise we'd silently destroy unsynced chats. That retention used to
 * be unbounded; we now also record `{ userId, signedOutAt }` here, and at
 * every sign-in:
 *
 *  1. The entry for the currently-signing-in user is removed (their data
 *     is no longer orphaned; normal sync resumes).
 *  2. Each remaining entry is opportunistically re-flushed (throttled by
 *     `lastAttemptAt`); on success the user's chat history + tokens are
 *     wiped.
 *  3. Any entry older than `PENDING_CLEANUP_MAX_AGE_MS` is wiped
 *     unconditionally, regardless of flush status, so the worst-case
 *     retention on a shared machine is bounded.
 */
export interface PendingCleanup {
  readonly userId: string;
  readonly signedOutAt: number;
  /** Last cleanup attempt — used to throttle retries. */
  readonly lastAttemptAt?: number;
}

/** Hard cap on retention. Data is wiped after this regardless of flush. */
export const PENDING_CLEANUP_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function loadPendingCleanups(): readonly PendingCleanup[] {
  try {
    const raw = localStorage.getItem(PENDING_CLEANUP_KEY);
    if (raw == null || raw.length === 0) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPersistedPendingCleanup);
  } catch {
    return [];
  }
}

function savePendingCleanups(entries: readonly PendingCleanup[]): void {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(PENDING_CLEANUP_KEY);
    } else {
      localStorage.setItem(PENDING_CLEANUP_KEY, JSON.stringify(entries));
    }
  } catch {
    // best-effort
  }
}

/**
 * Record (or refresh) a pending-cleanup entry for `userId`. Idempotent:
 * a repeat call for the same user updates `signedOutAt` to now and
 * clears the throttle.
 */
export function addPendingCleanup(userId: string): void {
  const lower = userId.toLowerCase();
  const others = loadPendingCleanups().filter((c) => c.userId !== lower);
  savePendingCleanups([...others, { userId: lower, signedOutAt: Date.now() }]);
}

export function removePendingCleanup(userId: string): void {
  const lower = userId.toLowerCase();
  const remaining = loadPendingCleanups().filter((c) => c.userId !== lower);
  savePendingCleanups(remaining);
}

export function updatePendingCleanup(
  userId: string,
  patch: Partial<PendingCleanup>,
): void {
  const lower = userId.toLowerCase();
  const all = loadPendingCleanups();
  const next = all.map((c) => (c.userId === lower ? { ...c, ...patch } : c));
  savePendingCleanups(next);
}

function readRawLegacyHistory(userId: string): string | null {
  const current = localStorage.getItem(legacyHistoryKey(userId));
  if (current != null) return current;
  const veryLegacy = localStorage.getItem(veryLegacyHistoryKey(userId));
  if (veryLegacy == null) return null;
  try {
    localStorage.setItem(legacyHistoryKey(userId), veryLegacy);
    localStorage.removeItem(veryLegacyHistoryKey(userId));
  } catch {
    // best-effort migration
  }
  return veryLegacy;
}

function parseMessages(raw: string | null): readonly ChatMessage[] {
  if (raw == null || raw.length === 0) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPersistedChatMessage);
  } catch {
    return [];
  }
}

export function loadSessions(userId: string): readonly ChatSession[] {
  try {
    const raw = localStorage.getItem(sessionsKey(userId));
    if (raw == null || raw.length === 0) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPersistedSession);
  } catch {
    return [];
  }
}

export function saveSessions(
  userId: string,
  sessions: readonly ChatSession[],
): void {
  try {
    localStorage.setItem(sessionsKey(userId), JSON.stringify(sessions));
  } catch {
    // best-effort
  }
}

export function loadCustomFolders(userId: string): readonly CustomFolder[] {
  try {
    const raw = localStorage.getItem(customFoldersKey(userId));
    if (raw == null || raw.length === 0) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPersistedCustomFolder);
  } catch {
    return [];
  }
}

export function saveCustomFolders(
  userId: string,
  folders: readonly CustomFolder[],
): void {
  try {
    localStorage.setItem(customFoldersKey(userId), JSON.stringify(folders));
  } catch {
    // best-effort
  }
}

export function loadSessionMessages(
  userId: string,
  sessionId: string,
): readonly ChatMessage[] {
  try {
    return parseMessages(
      localStorage.getItem(sessionMessagesKey(userId, sessionId)),
    );
  } catch {
    return [];
  }
}

export function saveSessionMessages(
  userId: string,
  sessionId: string,
  messages: readonly ChatMessage[],
): void {
  try {
    const trimmed = trimForPersistence(messages);
    localStorage.setItem(
      sessionMessagesKey(userId, sessionId),
      JSON.stringify(trimmed),
    );
  } catch {
    // best-effort
  }
}

export function deleteSessionMessages(userId: string, sessionId: string): void {
  try {
    localStorage.removeItem(sessionMessagesKey(userId, sessionId));
  } catch {
    // ignore
  }
}

export function loadHistoryUiState(userId: string): HistoryUiState {
  try {
    const raw = localStorage.getItem(uiStateKey(userId));
    if (raw == null || raw.length === 0) return DEFAULT_UI_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object') return DEFAULT_UI_STATE;
    const obj = parsed as Record<string, unknown>;
    const collapsed = obj.collapsed as Record<string, unknown> | undefined;
    const activeSessionId =
      typeof obj.activeSessionId === 'string' ? obj.activeSessionId : null;
    return {
      collapsed: {
        paradex: collapsed?.paradex === true,
        paradigm: collapsed?.paradigm === true,
      },
      activeSessionId,
    };
  } catch {
    return DEFAULT_UI_STATE;
  }
}

export function saveHistoryUiState(
  userId: string,
  state: HistoryUiState,
): void {
  try {
    localStorage.setItem(uiStateKey(userId), JSON.stringify(state));
  } catch {
    // ignore
  }
}

/**
 * Maximum stored length for an auto-derived session title. Generous on
 * purpose — the row's CSS ellipsis handles visual truncation per panel
 * width, so we keep enough characters to fill the row at any size and
 * cap only to keep a pathological multi-KB message from bloating the
 * sessions index.
 */
export const SESSION_TITLE_MAX_LEN = 200;

/**
 * Pull a recognizable title out of a transcript: the first user message,
 * collapsed to one line and clamped to `SESSION_TITLE_MAX_LEN`. Falls back
 * to the first assistant message when there's no user message yet, and to
 * `fallback` when there's no content at all.
 */
export function deriveSessionTitleFromMessages(
  messages: readonly ChatMessage[],
  fallback: string,
): string {
  const candidate =
    messages.find((m) => m.role === 'user' && m.content.trim().length > 0) ??
    messages.find((m) => m.content.trim().length > 0);
  if (candidate == null) return fallback;
  const oneLine = candidate.content.replace(/\s+/gu, ' ').trim();
  if (oneLine.length === 0) return fallback;
  if (oneLine.length <= SESSION_TITLE_MAX_LEN) return oneLine;
  return `${oneLine.slice(0, SESSION_TITLE_MAX_LEN - 1)}…`;
}

/**
 * If a legacy flat-history blob exists for this user but the new sessions
 * index doesn't, migrate it into a single session under the Paradigm folder
 * so existing users don't lose their transcript. The session title is
 * derived from the first user message so the Recents list shows real
 * context, not a generic "Restored chat" label.
 *
 * Returns the new sessions list when a migration ran, otherwise returns
 * the existing sessions list unchanged.
 */
export function migrateLegacyHistoryIfNeeded(
  userId: string,
): readonly ChatSession[] {
  const existing = loadSessions(userId);
  if (existing.length > 0) return existing;
  const legacy = readRawLegacyHistory(userId);
  if (legacy == null || legacy.length === 0) return existing;
  const messages = parseMessages(legacy);
  if (messages.length === 0) {
    // Legacy key exists but is empty/corrupt — clean it up so we don't
    // try to migrate again on the next load.
    try {
      localStorage.removeItem(legacyHistoryKey(userId));
    } catch {
      // ignore
    }
    return existing;
  }
  const now = Date.now();
  const session: ChatSession = {
    sessionId: generateSessionId(),
    folder: 'paradigm',
    title: deriveSessionTitleFromMessages(messages, 'Restored chat'),
    pinned: false,
    createdAt: now,
    updatedAt: messages[messages.length - 1]?.timestamp ?? now,
  };
  const migrated = [session];
  saveSessions(userId, migrated);
  saveSessionMessages(userId, session.sessionId, messages);
  try {
    localStorage.removeItem(legacyHistoryKey(userId));
  } catch {
    // ignore
  }
  return migrated;
}

/**
 * Recover folders referenced by sessions whose `customFolderId` points
 * at an id that's missing from the custom-folders index. Used when
 * the folder index gets wiped (same race window that orphaned the
 * chat_history blobs from the sessions index). We can't recover the
 * original folder name from storage — it was only ever in the folder
 * index that's now gone — so each placeholder is labeled
 * "Recovered folder N" for the user to rename.
 *
 * Returns the merged list (existing + recovered). When recovery
 * happened, callers should persist the result so subsequent loads
 * pick it up.
 */
export function recoverOrphanedFolders(
  sessions: readonly ChatSession[],
  existingFolders: readonly CustomFolder[],
): {
  readonly folders: readonly CustomFolder[];
  readonly recovered: boolean;
} {
  const existingIds = new Set(existingFolders.map((f) => f.id));
  const referencedIds = new Set<string>();
  for (const s of sessions) {
    if (s.customFolderId != null && !existingIds.has(s.customFolderId)) {
      referencedIds.add(s.customFolderId);
    }
  }
  if (referencedIds.size === 0) {
    return { folders: existingFolders, recovered: false };
  }
  const now = Date.now();
  let n = 1;
  const orphanFolders: CustomFolder[] = [];
  // Sort referenced ids stably so the recovery is deterministic across
  // reloads if it has to run again before the user renames anything.
  const sortedIds = [...referencedIds].sort();
  for (const id of sortedIds) {
    orphanFolders.push({
      id,
      name: `Recovered folder ${n}`,
      createdAt: now,
    });
    n += 1;
  }
  return {
    folders: [...existingFolders, ...orphanFolders],
    recovered: true,
  };
}

/**
 * Recover sessions whose `<userId>:paradigm_terminal_chat_history:<id>`
 * blob still exists in localStorage but whose entry is missing from
 * the sessions index. A regression where the index gets overwritten
 * with a one-element list (e.g. when state briefly resets to empty
 * between hydrations and the user creates a fresh chat in that
 * window) leaves the per-session message blobs orphaned. This helper
 * walks the storage, identifies orphans, and re-adds them to the
 * index with titles derived from their stored messages.
 *
 * Returns the merged list (existing + recovered). When recovery
 * happened, callers should persist the result so subsequent loads
 * pick it up.
 */
export function recoverOrphanedSessions(
  userId: string,
  existing: readonly ChatSession[],
): {
  readonly sessions: readonly ChatSession[];
  readonly recovered: boolean;
} {
  const lowerId = userId.toLowerCase();
  const prefix = `${lowerId}:${HISTORY_KEY_SUFFIX}:`;
  const existingIds = new Set(existing.map((s) => s.sessionId));
  const orphans: ChatSession[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key == null || !key.startsWith(prefix)) continue;
      const sessionId = key.slice(prefix.length);
      if (sessionId.length === 0) continue;
      if (existingIds.has(sessionId)) continue;
      const messages = loadSessionMessages(userId, sessionId);
      if (messages.length === 0) continue;
      const lastTs = messages[messages.length - 1]?.timestamp ?? Date.now();
      const firstTs = messages[0]?.timestamp ?? lastTs;
      orphans.push({
        sessionId,
        folder: 'paradigm',
        title: deriveSessionTitleFromMessages(messages, 'Recovered chat'),
        pinned: false,
        createdAt: firstTs,
        updatedAt: lastTs,
      });
    }
  } catch {
    // localStorage iteration failed — return what we have.
    return { sessions: existing, recovered: false };
  }
  if (orphans.length === 0) {
    return { sessions: existing, recovered: false };
  }
  // Newest first, then merge into the index.
  orphans.sort((a, b) => b.updatedAt - a.updatedAt);
  return { sessions: [...existing, ...orphans], recovered: true };
}

/**
 * Generate a session id that doubles as the gateway `sessionKey`. Uses
 * `crypto.randomUUID()` when available (HTTPS / modern browsers) and falls
 * back to a Date + Math.random() string otherwise.
 */
export function generateSessionId(): string {
  return generateRandomId('s');
}

/**
 * Generate a unique message id. Was `${kind}-${counter++}` seeded from
 * `Date.now()`, which collided across devices when two browser windows
 * happened to start in the same millisecond — `mergeMessages` unions by
 * id and silently drops one side on collision, so cross-device uniqueness
 * matters here. `prefix` is informational only.
 */
export function generateMessageId(prefix: string): string {
  return `${prefix}-${generateRandomId('m')}`;
}

function generateRandomId(fallbackPrefix: string): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c != null && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `${fallbackPrefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

/**
 * Pick the slice of `messages` we want to persist:
 *
 *   1. Drop any message older than `PERSISTED_HISTORY_MAX_AGE_MS` so the
 *      blob doesn't accumulate forever.
 *   2. Always keep at least `PERSISTED_HISTORY_MIN_MESSAGES` of the most
 *      recent messages — so a long-idle user still sees their last
 *      conversation on reload.
 *   3. Hard-cap the total at `PERSISTED_HISTORY_HARD_CAP`.
 *   4. Strip the streaming flag — a partially-streamed bubble persisted
 *      with `isStreaming: true` would reload as stuck dots.
 */
function trimForPersistence(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (messages.length === 0) return messages;

  const cutoff = Date.now() - PERSISTED_HISTORY_MAX_AGE_MS;
  const withinWindow = messages.filter((m) => m.timestamp > cutoff);

  const kept =
    withinWindow.length >= PERSISTED_HISTORY_MIN_MESSAGES
      ? withinWindow
      : messages.slice(-PERSISTED_HISTORY_MIN_MESSAGES);

  return kept
    .slice(-PERSISTED_HISTORY_HARD_CAP)
    .map((m) => (m.isStreaming === true ? { ...m, isStreaming: false } : m));
}

/**
 * Wipe everything associated with this user: the sessions index, every
 * per-session message blob, the UI state, and any leftover legacy keys.
 * Used by the sign-out and manual-disconnect cleanup paths.
 *
 * Sessions are first listed off the index, then deleted. As a defence
 * against a corrupt or out-of-sync index — which would otherwise leave
 * orphaned `<userId>:paradigm_terminal_chat_history:<sessionId>` blobs
 * in localStorage after sign-out — we follow up with a prefix scan over
 * `localStorage.key()` to catch any strays the index missed.
 */
export function clearChatHistory(userId: string): void {
  try {
    const sessions = loadSessions(userId);
    for (const s of sessions) {
      try {
        localStorage.removeItem(sessionMessagesKey(userId, s.sessionId));
      } catch {
        // ignore
      }
    }
    // Fallback prefix scan in case the sessions index was corrupt or
    // missing — we still don't want chat content to linger after the
    // user signs out. `localStorage.key()` is index-based and stable
    // for the duration of a single synchronous loop, but we collect
    // matches first because removal shifts indices.
    try {
      const prefix = `${userId.toLowerCase()}:${HISTORY_KEY_SUFFIX}:`;
      const orphanKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key != null && key.startsWith(prefix)) {
          orphanKeys.push(key);
        }
      }
      for (const key of orphanKeys) {
        try {
          localStorage.removeItem(key);
        } catch {
          // ignore
        }
      }
    } catch {
      // localStorage iteration unavailable — best-effort cleanup.
    }
    localStorage.removeItem(sessionsKey(userId));
    localStorage.removeItem(customFoldersKey(userId));
    localStorage.removeItem(uiStateKey(userId));
    localStorage.removeItem(legacyHistoryKey(userId));
    localStorage.removeItem(veryLegacyHistoryKey(userId));
    localStorage.removeItem(tombstonesKey(userId));
  } catch {
    // ignore
  }
}

function isPersistedTombstone(value: unknown): value is DeletionTombstone {
  if (value == null || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    (t.kind === 'session' || t.kind === 'folder') &&
    typeof t.id === 'string' &&
    typeof t.deletedAt === 'number'
  );
}

function isPersistedPendingCleanup(value: unknown): value is PendingCleanup {
  if (value == null || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.userId === 'string' &&
    c.userId.length > 0 &&
    typeof c.signedOutAt === 'number' &&
    Number.isFinite(c.signedOutAt) &&
    (c.lastAttemptAt === undefined || typeof c.lastAttemptAt === 'number')
  );
}

function isPersistedChatMessage(value: unknown): value is ChatMessage {
  if (value == null || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.id === 'string' &&
    (m.role === 'user' || m.role === 'assistant') &&
    typeof m.content === 'string' &&
    typeof m.timestamp === 'number'
  );
}

function isPersistedCustomFolder(value: unknown): value is CustomFolder {
  if (value == null || typeof value !== 'object') return false;
  const f = value as Record<string, unknown>;
  return (
    typeof f.id === 'string' &&
    typeof f.name === 'string' &&
    typeof f.createdAt === 'number' &&
    (f.pinned === undefined || typeof f.pinned === 'boolean')
  );
}

function isPersistedSession(value: unknown): value is ChatSession {
  if (value == null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  const folder: SessionFolder | null =
    s.folder === 'paradex' || s.folder === 'paradigm' ? s.folder : null;
  return (
    typeof s.sessionId === 'string' &&
    folder != null &&
    typeof s.title === 'string' &&
    typeof s.pinned === 'boolean' &&
    typeof s.createdAt === 'number' &&
    typeof s.updatedAt === 'number' &&
    (s.customFolderId === undefined || typeof s.customFolderId === 'string') &&
    (s.clearedAt === undefined || typeof s.clearedAt === 'number')
  );
}
